'use client'

import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react'
import { Bars3Icon, ChevronDoubleRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import _ from 'lodash'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { Fragment } from 'react'
import { twMerge } from 'tailwind-merge'
import { Divider, Logo, Separator } from '@/components'
import { menu } from '@/settings'
import { useGlobalStore } from '@/store/provider'
import { AdminUser } from './adminUser'
import Navigation from './navigation'
import ThemeToggle from './other/themeToggle'
import SignOut from './signOut'

const NavigationComponent = dynamic(() => import('@/components/navigation'), {
  ssr: false,
  loading: () => <p>...</p>,
})

export default function DefaultLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { navOpen, setNavOpen } = useGlobalStore((state) => state)
  return (
    <div className='h-full w-full dark:text-zinc-300'>
      <Dialog open={navOpen} onClose={setNavOpen} className='relative z-50 lg:hidden shadow-lg'>
        <DialogBackdrop
          transition
          className='fixed inset-0 bg-zinc-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0'
        />
        <div className='fixed inset-0 flex'>
          <DialogPanel
            transition
            className='relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full'
          >
            <TransitionChild>
              <div className='absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0'>
                <button type='button' onClick={() => setNavOpen(false)} className='-m-2.5 p-2.5'>
                  <span className='sr-only'>Close sidebar</span>
                  <XMarkIcon aria-hidden='true' className='h-6 w-6 text-white' />
                </button>
              </div>
            </TransitionChild>
            <div className='flex grow flex-col gap-y-2 overflow-y-auto bg-white dark:bg-zinc-950 pb-4'>
              <Logo size='w-40' className='pt-8' />
              <Divider />
              <div className='px-6'>
                <Navigation pathname={pathname} />
              </div>
              <AdminUser />
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <div
        className={twMerge(
          'relative hidden',
          'lg:fixed lg:w-80 lg:inset-y-0 lg:z-50 lg:flex lg:flex-col border-t',
          'grow flex-col gap-y-2 overflow-y-auto',
          'bg-white dark:bg-zinc-950 border-r transitio shadow',
          'scrollbar-w-2 scrollbar scrollbar-thumb-transparent scrollbar-thumb-rounded-full',
          'hover:scrollbar-thumb-zinc-300 dark:hover:scrollbar-thumb-zinc-700',
        )}
      >
        <Logo size='w-34' className='pt-8 pb-4' />
        <div className='py-4 px-6'>
          <NavigationComponent pathname={pathname} />
        </div>
        <AdminUser />
      </div>
      <div className='lg:pl-80'>
        {/* Header */}
        <div
          className={twMerge(
            'sticky top-0 z-40 flex h-20 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8',
            'transition shadow border-b border-t',
            'items-center justify-center backdrop-blur-md dark:backdrop-blur-sm',
            'bg-zinc-100 dark:bg-zinc-950',
          )}
        >
          <button type='button' onClick={() => setNavOpen(true)} className='-m-2.5 p-2.5 text-zinc-700 dark:text-zinc-200 lg:hidden'>
            <Bars3Icon className='h-6 w-6' />
          </button>
          <div className='h-6 w-px bg-zinc-200 lg:hidden' />
          <Logo size='w-28' className='lg:hidden' />
          <div className='flex flex-1 gap-x-4 self-stretch lg:gap-x-6 items-center justify-end lg:justify-between'>
            <h1 className='font-bold text-xl leading-8 hidden lg:block'>
              <Title pathname={pathname} />
            </h1>
            <div className='flex items-center gap-x-2'>
              <ThemeToggle />
              <Separator />
              <SignOut />
            </div>
          </div>
        </div>
        {/* Main */}
        <main className='px-8 pt-6 pb-12'>{children}</main>
      </div>
    </div>
  )
}

function Title({ pathname }: { pathname: string }) {
  let d: string[] = []
  _.forEach(menu, (x) => {
    if (x.href === pathname) {
      const z = x?.path
      if (z) {
        d = z
        return false
      }
    }
    if (x.children) {
      const z = _.find(x.children, (y) => y.href === pathname)?.path
      if (z) {
        d = z
        return false
      }
    }
  })
  if (_.isEmpty(d)) {
    d = _.chain(pathname).split('/').compact().value()
  }
  return (
    <div className='flex flex-1 items-center'>
      {_.map(d, (x, i) => {
        return (
          <Fragment key={i}>
            <div>{_.startCase(x)}</div>
            {i < d.length - 1 && <ChevronDoubleRightIcon className='h-4 w-4 mx-2' />}
          </Fragment>
        )
      })}
    </div>
  )
}
